import React, { useMemo, useRef, useState } from "react";
import styles from "./ChangeOrganizationNameModal.module.scss";
import { BaseModal } from "@whatslly/ui-components/modals/BaseModal";
import { IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";
import { useTranslation } from "react-i18next";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import { ORG_TYPE_OPTIONS } from "@whatslly/commons/constants/organizationTypes";
import { orgConfigsObject } from "@whatslly/commons/OrgConfigsFeatures";
import { orgPartnerValues } from "@whatslly/types/OrgPartner";
import type { OrgConfigs } from "@whatslly/types/OrgConfigs";
import { Switch } from "@whatslly/ui-components/v2/BaseElements/Switch";
import { DatePicker } from "@whatslly/ui-components/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useTheme } from "../../../../../services/useTheme";

export type OrgIdentityFormState = {
  isActive: boolean;
  isPOC: boolean;
  POCExpiration: string;
  org_partner: string;
  defaultLanguage: string;
  serverlocation: string;
  available_crm: string;
};

function buildIdentityState(cfg?: Partial<OrgConfigs>): OrgIdentityFormState {
  const d = orgConfigsObject;
  return {
    isActive: typeof cfg?.isActive === "boolean" ? cfg.isActive : (d.isActive.defaultValue as boolean),
    isPOC: typeof cfg?.isPOC === "boolean" ? cfg.isPOC : (d.isPOC.defaultValue as boolean),
    POCExpiration: typeof cfg?.POCExpiration === "string" ? cfg.POCExpiration : "",
    org_partner: (cfg?.org_partner ?? d.org_partner.defaultValue) as string,
    defaultLanguage: (cfg?.defaultLanguage ?? d.defaultLanguage.defaultValue) as string,
    serverlocation: (cfg?.serverlocation ?? d.serverlocation.defaultValue) as string,
    available_crm: (cfg?.available_crm as string) ?? "none",
  };
}

export type ChangeOrganizationModalResult = {
  name: string;
  description?: string;
  orgType?: { label: string; value: string };
} & OrgIdentityFormState;

interface IChangeOrganizationNameModalProps extends IModalInstance<ChangeOrganizationModalResult> {
  orgName: string;
  orgDescription?: string;
  orgType?: { label: string; value: string };
  initialIdentityFromServer?: Partial<OrgConfigs>;
}

const AVAILABLE_CRM_OPTIONS = ["none", ...orgConfigsObject.available_crm.options] as const;

export const ChangeOrganizationNameModal: React.FC<IChangeOrganizationNameModalProps> = ({
  closeModal,
  orgName,
  orgDescription,
  orgType: initialOrgType,
  initialIdentityFromServer,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [newOrgName, setNewOrgName] = useState(orgName);
  const [description, setDescription] = useState(orgDescription);
  const [orgType, setOrgType] = useState<{ label: string; value: string } | null>(() => {
    if (initialOrgType) return initialOrgType;
    const fromCfg = initialIdentityFromServer?.orgType;
    if (fromCfg) {
      const opt = ORG_TYPE_OPTIONS.find((o) => o.value === fromCfg);
      if (opt) return opt;
    }
    return null;
  });

  const [identity, setIdentity] = useState(() => buildIdentityState(initialIdentityFromServer));

  const baselineRef = useRef<{
    name: string;
    description: string;
    orgTypeValue: string | null;
    identity: OrgIdentityFormState;
  }>({
    name: orgName,
    description: orgDescription ?? "",
    orgTypeValue: initialOrgType?.value ?? (initialIdentityFromServer?.orgType as string | undefined) ?? null,
    identity: buildIdentityState(initialIdentityFromServer),
  });

  const defaultLanguageOptions = useMemo(
    () =>
      orgConfigsObject.defaultLanguage.options.map((option) => ({
        label: t(`super_admin_org_configs.configs.options.defaultLanguage.${option}`, { defaultValue: option }),
        value: option,
      })),
    [t]
  );

  const serverLocationOptions = useMemo(
    () =>
      orgConfigsObject.serverlocation.options.map((option) => ({
        label: t(`super_admin_org_configs.configs.options.serverlocation.${option}`, { defaultValue: option }),
        value: option,
      })),
    [t]
  );

  const availableCrmOptions = useMemo(
    () =>
      AVAILABLE_CRM_OPTIONS.map((option) => ({
        label: t(`super_admin_org_configs.configs.options.available_crm.${option}`, { defaultValue: option }),
        value: option,
      })),
    [t]
  );

  const orgPartnerOptions = useMemo(() => orgPartnerValues.map((option) => ({ label: option, value: option })), []);

  const isValid = useMemo(() => {
    const b = baselineRef.current;
    const identityDirty =
      identity.isActive !== b.identity.isActive ||
      identity.isPOC !== b.identity.isPOC ||
      identity.POCExpiration !== b.identity.POCExpiration ||
      identity.org_partner !== b.identity.org_partner ||
      identity.defaultLanguage !== b.identity.defaultLanguage ||
      identity.serverlocation !== b.identity.serverlocation ||
      identity.available_crm !== b.identity.available_crm;

    const hasChanges = newOrgName !== b.name || (description ?? "") !== b.description || (orgType?.value ?? null) !== b.orgTypeValue || identityDirty;

    const hasRequiredFields = newOrgName.trim() !== "" && orgType !== null;
    return hasChanges && hasRequiredFields;
  }, [newOrgName, description, orgType, identity]);

  const pocDate = identity.POCExpiration ? dayjs(identity.POCExpiration) : null;
  const pocPickerValue = pocDate?.isValid() ? pocDate : null;

  return (
    <BaseModal
      header={<ModalHeader closeModal={closeModal}>{t("search_orgs.would_you_like_to_change_organization_name")}</ModalHeader>}
      closeOnClickOutside={false}
      closeOnEscape={false}
      closeModal={closeModal}
      className={styles.modal}
      bodyClassName={styles.body}
      backdropClassName={styles.backdrop}
    >
      <div className={styles.section}>
        <label htmlFor="org-name" className={styles.label}>
          {t("search_orgs.organization_name")}
        </label>
        <TextInput
          placeholder={t("search_orgs.organization_name")}
          value={newOrgName}
          paddingInline="none"
          onChange={(e) => setNewOrgName(e.target.value)}
          data-testid="org-name"
        />
        <label htmlFor="org-type" className={styles.label}>
          {t("user_management.invite_user_modal.org_type")} *
        </label>
        <Select
          value={orgType}
          onChange={(value: unknown) => setOrgType(value as { label: string; value: string } | null)}
          paddingInline="none"
          options={ORG_TYPE_OPTIONS}
          placeholder={t("user_management.invite_user_modal.org_type")}
          data-testid="org-type-select"
        />
        <label htmlFor="org-description" className={styles.label}>
          {t("search_orgs.organization_description")}
        </label>
        <TextInput
          name="org-description"
          placeholder={t("search_orgs.organization_description")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          paddingInline="none"
          data-testid="org-description"
        />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t("search_orgs.edit_organization_identity_section")}</div>

        <div className={styles.row}>
          <span className={styles.switchLabel}>{t("super_admin_org_configs.configs.names.isActive")}</span>
          <Switch
            testId="org-modal-is-active"
            checked={identity.isActive}
            onChange={(e) => setIdentity((prev) => ({ ...prev, isActive: e.target.checked }))}
          />
        </div>
        <div className={styles.row}>
          <span className={styles.switchLabel}>{t("super_admin_org_configs.configs.names.isPOC")}</span>
          <Switch
            testId="org-modal-is-poc"
            checked={identity.isPOC}
            onChange={(e) => setIdentity((prev) => ({ ...prev, isPOC: e.target.checked }))}
          />
        </div>
        {identity.isPOC ? (
          <>
            <label className={styles.label}>{t("super_admin_org_configs.configs.names.POCExpiration")}</label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                onChange={(date) => {
                  const isValidDate = date !== null && date.isValid();
                  setIdentity((prev) => ({ ...prev, POCExpiration: isValidDate ? date!.toISOString() : "" }));
                }}
                value={pocPickerValue}
                darkMode={theme === "dark"}
              />
            </LocalizationProvider>
          </>
        ) : null}

        <label className={styles.label}>{t("super_admin_org_configs.configs.names.org_partner")}</label>
        <Select
          value={orgPartnerOptions.find((o) => o.value === identity.org_partner) ?? null}
          onChange={(value: unknown) => {
            const v = value as { value: string } | null;
            setIdentity((prev) => ({ ...prev, org_partner: v?.value ?? orgConfigsObject.org_partner.defaultValue! }));
          }}
          paddingInline="none"
          options={orgPartnerOptions}
          data-testid="org-modal-org-partner"
        />

        <label className={styles.label}>{t("super_admin_org_configs.configs.names.defaultLanguage")}</label>
        <Select
          value={defaultLanguageOptions.find((o) => o.value === identity.defaultLanguage) ?? null}
          onChange={(value: unknown) => {
            const v = value as { value: string } | null;
            setIdentity((prev) => ({ ...prev, defaultLanguage: v?.value ?? orgConfigsObject.defaultLanguage.defaultValue! }));
          }}
          paddingInline="none"
          options={defaultLanguageOptions}
          data-testid="org-modal-default-language"
        />

        <label className={styles.label}>{t("super_admin_org_configs.configs.names.serverlocation")}</label>
        <Select
          value={serverLocationOptions.find((o) => o.value === identity.serverlocation) ?? null}
          onChange={(value: unknown) => {
            const v = value as { value: string } | null;
            setIdentity((prev) => ({ ...prev, serverlocation: v?.value ?? orgConfigsObject.serverlocation.defaultValue! }));
          }}
          paddingInline="none"
          options={serverLocationOptions}
          data-testid="org-modal-server-location"
        />

        <label className={styles.label}>{t("super_admin_org_configs.configs.names.available_crm")}</label>
        <Select
          value={availableCrmOptions.find((o) => o.value === identity.available_crm) ?? null}
          onChange={(value: unknown) => {
            const v = value as { value: string } | null;
            setIdentity((prev) => ({ ...prev, available_crm: v?.value ?? "none" }));
          }}
          paddingInline="none"
          options={availableCrmOptions}
          data-testid="org-modal-available-crm"
        />
      </div>

      <ModalButtons
        isSubmitDisabled={!isValid}
        testId="modal-buttons-submit-button"
        submitText={t("search_orgs.change")}
        onCancel={closeModal}
        onSubmit={() =>
          closeModal({
            name: newOrgName,
            description,
            orgType: orgType ?? undefined,
            ...identity,
            POCExpiration: identity.isPOC ? identity.POCExpiration : "",
          })
        }
      />
    </BaseModal>
  );
};
